// One outer card holding several related settings sections.
//
// It only supplies chrome, a heading, an optional collapse and an optional setup
// tag; every child stays the component it was, so nothing here knows what a Gmail
// sync or an inbox list is.
import { useState, type ReactNode } from "react";

import type { SetupState } from "../state/useProjectSetupSteps";
import styles from "./SettingsGroup.module.css";
import { Chevron } from "./Chevron";

export const SettingsGroup = ({
  id,
  title,
  subtitle,
  collapsible = false,
  setup,
  children,
}: {
  id?: string;
  title: string;
  subtitle?: string;
  /** Starts collapsed, opened from a caret on the title. */
  collapsible?: boolean;
  /** Tags the group as set up or not; the note says what is missing. */
  setup?: { state: SetupState; note: string };
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(!collapsible);
  const incomplete = setup?.state === "incomplete";
  return (
    <section
      id={id}
      className={incomplete ? `${styles.group} ${styles.incomplete}` : styles.group}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>
          {collapsible ? (
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              <span className={styles.caret} aria-hidden="true">
                <Chevron tree open={open} />
              </span>
              {title}
            </button>
          ) : (
            title
          )}
          {setup && (
            <span className={incomplete ? styles.tagIncomplete : styles.tagComplete}>
              {incomplete ? "Incomplete" : "Connected"}
            </span>
          )}
        </h2>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
      </header>
      {incomplete && <p className={styles.setupNote}>{setup.note}</p>}
      {open && <div className={styles.body}>{children}</div>}
    </section>
  );
};
